import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputAdornment from '@mui/material/InputAdornment';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import makeStyles from '@mui/styles/makeStyles';
import React, { useState } from 'react';
import { handleUnauthorizedResponse } from '../../api/http';
import { AuthContext, AuthData } from '../../context/auth';
import { ClaimContext, ClaimData } from '../../context/claim';
import { PolicyContext, PolicyData } from '../../context/policy';
import {
	ADD_PAYABLE_MODAL_ADD_PAYABLE_ACTION_TEXT,
	ADD_PAYABLE_MODAL_CANCEL_ACTION_TEXT,
	ADD_PAYABLE_MODAL_ERROR_TOAST_MSG,
	ADD_PAYABLE_MODAL_HEADING_TEXT,
	ADD_PAYABLE_MODAL_SUCCESS_TOAST_MSG,
} from '../../data/labelsAndMessages';
import {
	SubClaimPayableCreateRequest,
	SubClaimResponse,
} from '../../types/claim';
import { getCurrencySymbol } from '../../util/util';
import DefaultDialog from '../DefaultDialog';
import { errorToast, successToast } from '../Toast';
import {
	checkInvalidPayableValue,
	formatRequestedPayable,
	formatReserve,
	getPayableError,
} from './util';

interface Props {
	authData: AuthData;
	claimData: ClaimData;
	subclaim: SubClaimResponse;
	policyData: PolicyData;
	openOverride?: boolean;
}

const useStyles = makeStyles((theme) => ({
	labelAsterisk: {
		color: 'red',
	},
}));

const AddPayableModal = ({
	authData,
	claimData,
	subclaim,
	policyData,
	openOverride = false,
}: Props) => {
	const classes = useStyles();
	const { claim } = claimData;

	const [isOpen, setIsOpen] = useState(openOverride);
	const [payableType, setPayableType] = useState('loss');
	const [amount, setAmount] = useState('0');
	const [recipient, setRecipient] = useState('');
	const [comment, setComment] = useState('');

	React.useEffect(() => {
		// when switching payable type -> reset amount, recipient, comment
		setAmount('0');
		setRecipient('');
		setComment('');
	}, [payableType]);

	const availableReserve =
		payableType === 'loss'
			? formatReserve(subclaim.lossReserve.currentAmount, true)
			: formatReserve(subclaim.expenseReserve.currentAmount, true);

	const addingDisabled = checkInvalidPayableValue(amount, availableReserve);

	const close = () => {
		setIsOpen(false);
		// reset state
		setPayableType('loss');
		setAmount('0');
		setRecipient('');
		setComment('');
	};
	const open = () => setIsOpen(true);

	const confirmAddPayable = async () => {
		const payload: SubClaimPayableCreateRequest = {
			subClaimLocator: subclaim.locator,
			reserveType: payableType,
			amount: formatRequestedPayable(amount),
			recipient,
			comment,
		};
		try {
			await claimData.createSubclaimPayable(claim.locator, payload);
			close();
			successToast(ADD_PAYABLE_MODAL_SUCCESS_TOAST_MSG());
		} catch (error) {
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
			errorToast(ADD_PAYABLE_MODAL_ERROR_TOAST_MSG());
		}
	};

	const heading = ADD_PAYABLE_MODAL_HEADING_TEXT();
	const actions = [
		{
			text: ADD_PAYABLE_MODAL_ADD_PAYABLE_ACTION_TEXT(),
			onClick: addingDisabled ? null : confirmAddPayable,
		},
		{ text: ADD_PAYABLE_MODAL_CANCEL_ACTION_TEXT(), onClick: close },
	];

	const currencyVal = getCurrencySymbol(
		policyData && policyData.policy ? policyData.policy.currency : null,
	);

	const renderLossExpenseSelector = () => {
		return (
			<RadioGroup
				row
				name='lossExpenseSelector'
				value={payableType}
				onChange={(e) => setPayableType(e.target.value)}
			>
				<FormControlLabel
					value='loss'
					control={<Radio color='primary' />}
					label='Loss'
					labelPlacement='end'
				/>
				<FormControlLabel
					value='expense'
					control={<Radio color='primary' />}
					label='Expense'
					labelPlacement='end'
				/>
			</RadioGroup>
		);
	};

	const renderAmountInput = () => {
		return (
			<div style={{ marginTop: '5px' }}>
				<TextField
					id='payableAmountInput'
					error={addingDisabled}
					required
					type='number'
					style={{ width: '30%', marginRight: '10px' }}
					label={'Payable Amount'}
					value={amount}
					disabled={false}
					onChange={(e) => setAmount(e.target.value)}
					onBlur={() => setAmount(formatReserve(amount))}
					helperText={
						addingDisabled ? getPayableError(amount, availableReserve) : null
					}
					InputProps={{
						startAdornment: (
							<InputAdornment position='start'>{currencyVal}</InputAdornment>
						),
					}}
					InputLabelProps={{
						classes: {
							asterisk: classes.labelAsterisk,
						},
					}}
				/>
				<TextField
					id='availableReserve'
					type='number'
					style={{ width: '30%', marginRight: '10px' }}
					label={'Available Reserves'}
					value={availableReserve}
					disabled
					InputProps={{
						startAdornment: (
							<InputAdornment position='start'>{currencyVal}</InputAdornment>
						),
					}}
				/>
			</div>
		);
	};

	const renderRecipientInput = () => {
		return (
			<TextField
				id='recipientInput'
				style={{ width: '50%', marginRight: '10px', marginTop: '5px' }}
				label={'Recipient'}
				value={recipient}
				disabled={false}
				onChange={(e) => setRecipient(e.target.value)}
			/>
		);
	};

	const renderCommentInput = () => {
		return (
			<TextField
				id='commentInput'
				multiline
				maxRows={4}
				style={{ width: '100%', marginRight: '10px', marginTop: '5px' }}
				label={'Comment'}
				value={comment}
				disabled={false}
				onChange={(e) => setComment(e.target.value)}
			/>
		);
	};

	const renderModalContent = () => {
		return (
			<div style={{ display: 'flex', flexDirection: 'column' }}>
				{renderLossExpenseSelector()}
				{renderAmountInput()}
				{renderRecipientInput()}
				{renderCommentInput()}
			</div>
		);
	};

	return (
		<>
			<Button color='primary' variant='contained' onClick={open}>
				Add Payable
			</Button>
			<DefaultDialog
				actions={actions}
				open={isOpen}
				onClose={close}
				title={heading}
			>
				{renderModalContent()}
			</DefaultDialog>
		</>
	);
};

function withContext(Component: any) {
	return (props: Omit<Props, 'authData' | 'claimData' | 'policyData'>) => (
		<AuthContext.Consumer>
			{(authData) => (
				<PolicyContext.Consumer>
					{(policyData) => (
						<ClaimContext.Consumer>
							{(claimData) => (
								<Component
									{...props}
									claimData={claimData}
									authData={authData}
									policyData={policyData}
								/>
							)}
						</ClaimContext.Consumer>
					)}
				</PolicyContext.Consumer>
			)}
		</AuthContext.Consumer>
	);
}

export default withContext(AddPayableModal);
